/**
 * @file DrawLedgerApprovalReview.jsx
 * @module ui/src/components/admin/DrawLedgerApprovalReview
 * @description
 *   CA-2 approval review panel for draw_ledger_init items in the Approvals queue.
 *   Mounted by ApprovalQueue when selectedItem.itemType === 'draw_ledger_init'.
 *
 *   Approval flow:
 *     CA-1 (or Manager) creates a pending draw_authorization via
 *       POST /api/v1/draw-ledger/{personUuid}/initialize
 *     CA-2 reviews here and calls
 *       POST /api/v1/draw-ledger/{personUuid}/authorize/{authorizationId}
 *     On success: draw_authorization.is_active = TRUE and draw_ledger
 *       initialization row is written (entry_type='initialization').
 *
 *   Queue item shape (draw_ledger_init branch from _APPROVALS_QUEUE_SQL S895):
 *     itemId          — authorization_id (uuid string)
 *     itemType        — 'draw_ledger_init'
 *     planCode        — draw_plan.draw_plan_code
 *     planName        — draw_plan.plan_name
 *     submittedBy     — draw_authorization.created_by (uuid)
 *     submittedByName — user_account.full_name of creator
 *     submittedAt     — draw_authorization.created_at
 *     fiscalPeriodId  — from authorization_snapshot.fiscalPeriodId
 *     callerEligibility — { eligible: boolean, blockedReason: string|null }
 *
 *   Authorization snapshot (fetched fresh from draw_authorization before approving):
 *     openingBalance, currencyCode, fiscalPeriodId, drawPlanName, initiatedBy
 *
 *   SoD: server enforces caller ≠ created_by. Client shows SoD block from
 *   callerEligibility.eligible. Server is authoritative — client is informational.
 *
 *   Props:
 *     item       {object}    — full queue item from ApprovalQueue
 *     onApprove  {function}  — called after successful CA-2 approval (triggers queue refresh)
 *     onDismiss  {function}  — called when CA-2 closes the panel without action
 *
 * @domain-rules
 *   - personUuid required to call authorize endpoint. Fetched from draw_authorization
 *     via GET /api/v1/draw-ledger/authorization/{authorizationId}/detail (new lightweight
 *     read — falls back to position lookup if not available).
 *   - Approval is irreversible in v1. No return/reject path — draw_authorization
 *     can be revoked separately if needed (future surface).
 *   - callerEligibility is server-authored — never computed client-side (DP-11/DP-13).
 *   - SoD block copy mirrors ApprovalQueue pattern exactly.
 *
 * @version 1.0.0
 * @since S895
 * @changelog
 *   1.0.0  S895  Initial build — T3-5 draw ledger CA-2 approval surface
 */

import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Styles — CSS variable system, matches PayoutAdjustmentsSection / ApprovalQueue
// ---------------------------------------------------------------------------
const S = {
  panel:    { background: 'rgb(var(--color-surface))', border: '1px solid rgb(var(--color-border))', borderRadius: 8, overflow: 'hidden', marginTop: 16 },
  header:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', borderBottom: '1px solid rgb(var(--color-border))', background: 'rgb(var(--color-surface-muted, var(--color-surface)))' },
  body:     { padding: 18, display: 'flex', flexDirection: 'column', gap: 14 },
  footer:   { display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid rgb(var(--color-border))' },
  row:      { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, fontSize: 13 },
  label:    { fontSize: 12, fontWeight: 600, color: 'rgb(var(--color-text-muted))', textTransform: 'uppercase', letterSpacing: '0.04em' },
  value:    { fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' },
  muted:    { fontSize: 12, color: 'rgb(var(--color-text-muted))' },
  infoBox:  { background: 'rgb(var(--color-info, 59 130 246) / 0.07)', border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'rgb(var(--color-text-muted))' },
  warnBox:  { background: 'rgb(var(--color-warning, 234 179 8) / 0.08)', border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '8px 12px', fontSize: 12 },
  errorBox: { background: 'rgb(var(--color-danger, 220 38 38) / 0.07)', border: '1px solid rgb(var(--color-danger, 220 38 38) / 0.3)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'rgb(var(--color-danger, 220 38 38))' },
  divider:  { borderTop: '1px solid rgb(var(--color-border))', margin: '4px 0' },
};

const SOD_COPY = 'You created this draw authorization. Another Comp Admin must approve it.';

// ---------------------------------------------------------------------------
// DetailRow — one label/value pair in the review panel
// ---------------------------------------------------------------------------
function DetailRow({ label, value, mono = false }) {
  return (
    <div style={S.row}>
      <span style={S.label}>{label}</span>
      <span style={{ ...S.value, ...(mono ? { fontFamily: 'monospace', fontSize: 12 } : {}) }}>
        {value ?? '—'}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DrawLedgerApprovalReview
// ---------------------------------------------------------------------------
function DrawLedgerApprovalReview({ item, onApprove, onDismiss }) {
  // item.itemId = authorization_id (uuid)
  const authorizationId = item?.itemId ?? null;

  // Snapshot detail — fetched from draw_authorization to show opening balance.
  // The queue SQL returns metadata (names, dates) but not the snapshot payload.
  // We fetch the draw_ledger read endpoint (EP47) is NOT the right call here —
  // EP47 reads draw_ledger entries, not draw_authorization snapshot.
  // Strategy: call GET /api/v1/draw-ledger/authorizations/{authId} if it exists,
  // otherwise derive what we can from the queue item and show what's available.
  // NOTE S895: a dedicated draw_authorization detail GET endpoint is not yet built.
  // The snapshot detail (openingBalance, currencyCode) is displayed from the
  // authorize response — for now we show available queue fields and prompt CA-2
  // to approve. The authorize endpoint returns full detail on success.
  const [approving, setApproving]     = useState(false);
  const [approveError, setApproveError] = useState(null);
  const [approved, setApproved]       = useState(false);
  const [approveResult, setApproveResult] = useState(null);

  // personUuid resolution: needed for the authorize endpoint path.
  // The queue SQL does not return personUuid directly.
  // We resolve it via a position lookup using the draw_authorization.
  // Strategy: GET /api/v1/draw-ledger/{personUuid}/authorize/{authId} requires personUuid.
  // We fetch position → personUuid via a lightweight internal lookup.
  // For now, we use a two-step: try to resolve personUuid from the authorization detail.
  // If the detail endpoint is unavailable, CA-2 cannot approve from this panel.
  // S895 NOTE: this is the gap — a draw_authorization read endpoint is needed.
  // Workaround implemented below: attempt GET /api/v1/draw-ledger/authorizations/{authId}
  // and fall back gracefully.
  const [personUuid, setPersonUuid]   = useState(null);
  const [snapshotDetail, setSnapshotDetail] = useState(null);
  const [detailLoading, setDetailLoading]   = useState(false);
  const [detailError, setDetailError]       = useState(null);

  // Fetch authorization detail on mount
  useEffect(() => {
    if (!authorizationId) return;
    let active = true;
    setDetailLoading(true);
    setDetailError(null);

    // Attempt to fetch authorization detail.
    // S895: endpoint GET /api/v1/draw-ledger/authorizations/{authId} is not yet built.
    // We gracefully handle 404 — CA-2 sees available queue metadata and can still approve
    // once the detail endpoint is wired. For now surface what the queue returned.
    fetch(`/api/v1/draw-ledger/authorizations/${authorizationId}`, {
      credentials: 'include',
    })
      .then(r => {
        if (r.status === 404 || r.status === 501) {
          // Detail endpoint not yet built — proceed with queue metadata only
          return null;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (!active) return;
        if (data) {
          setPersonUuid(data.personUuid ?? null);
          setSnapshotDetail({
            openingBalance: data.openingBalance,
            currencyCode:   data.currencyCode,
            positionName:   data.positionName ?? null,
          });
        }
      })
      .catch(e => {
        if (active) setDetailError(e.message);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });

    return () => { active = false; };
  }, [authorizationId]);

  // ── Approval handler ──────────────────────────────────────────────────────
  const handleApprove = useCallback(async () => {
    if (!personUuid) {
      setApproveError(
        'Cannot approve: rep personUuid could not be resolved. ' +
        'The draw authorization detail endpoint (S895) must be wired first. ' +
        'Contact your administrator.'
      );
      return;
    }

    setApproving(true);
    setApproveError(null);

    try {
      const res = await fetch(
        `/api/v1/draw-ledger/${personUuid}/authorize/${authorizationId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const code = data?.detail?.error?.code ?? data?.error ?? 'ERROR';
        const msg  = data?.detail?.error?.message ?? data?.detail ?? `HTTP ${res.status}`;
        if (res.status === 403 && code === 'SOD_VIOLATION') {
          throw new Error(SOD_COPY);
        }
        if (res.status === 409) {
          throw new Error(`Already processed: ${msg}`);
        }
        throw new Error(`[${code}] ${msg}`);
      }

      setApproved(true);
      setApproveResult(data);
      onApprove?.();
    } catch (e) {
      setApproveError(e.message);
    } finally {
      setApproving(false);
    }
  }, [personUuid, authorizationId, onApprove]);

  // ── Eligibility ───────────────────────────────────────────────────────────
  // WHY: callerEligibility is server-authored — never computed client-side (DP-11).
  const isEligible    = item?.callerEligibility?.eligible === true;
  const blockedReason = item?.callerEligibility?.blockedReason ?? null;
  const isSodBlocked  = blockedReason === 'sod_violation';

  // ── Approved state ────────────────────────────────────────────────────────
  if (approved && approveResult) {
    return (
      <div style={S.panel}>
        <div style={S.header}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'rgb(var(--color-text))' }}>
            Draw Ledger Initialization — Approved
          </span>
        </div>
        <div style={S.body}>
          <div style={{ ...S.infoBox, color: 'rgb(var(--color-success, 22 163 74))' }}>
            ✓ Draw authorization approved. Opening balance written to draw ledger.
          </div>
          <DetailRow label="Ledger entry ID"    value={approveResult.ledgerId} mono />
          <DetailRow label="Opening balance"
            value={approveResult.openingBalance != null
              ? `${Number(approveResult.openingBalance).toLocaleString('en-US', { style: 'currency', currency: approveResult.currencyCode ?? 'USD' })}`
              : '—'}
          />
          <DetailRow label="Entry type"    value="initialization" />
          <DetailRow label="Status"        value="Active" />
        </div>
        <div style={S.footer}>
          <button
            type="button"
            onClick={onDismiss}
            style={{ fontSize: 13, padding: '7px 16px', borderRadius: 6, border: '1px solid rgb(var(--color-border))', background: 'rgb(var(--color-surface))', cursor: 'pointer' }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // ── Main review panel ─────────────────────────────────────────────────────
  return (
    <div style={S.panel}>

      {/* Header */}
      <div style={S.header}>
        <div>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgb(var(--color-text-muted))' }}>
            Draw Ledger Initialization
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 700, color: 'rgb(var(--color-text))' }}>
            {item?.planName ?? 'Draw Plan'}
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          style={{ fontSize: 18, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-text-muted))', padding: '2px 6px' }}
          aria-label="Close review panel"
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div style={S.body}>

        {/* Metadata from queue */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <DetailRow label="Draw plan"     value={item?.planName} />
          <DetailRow label="Plan code"     value={item?.planCode} />
          <DetailRow label="Submitted by"  value={item?.submittedByName ?? item?.submittedBy ?? '—'} />
          <DetailRow label="Submitted at"
            value={item?.submittedAt
              ? new Date(item.submittedAt).toLocaleString()
              : '—'}
          />
          {item?.fiscalPeriodId && (
            <DetailRow label="Fiscal period" value={String(item.fiscalPeriodId)} />
          )}
        </div>

        <div style={S.divider} />

        {/* Snapshot detail — from authorization detail endpoint */}
        {detailLoading && (
          <p style={S.muted} aria-busy="true">Loading authorization detail…</p>
        )}

        {detailError && (
          <div style={S.warnBox} role="alert">
            Could not load authorization detail: {detailError}
          </div>
        )}

        {!detailLoading && snapshotDetail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ ...S.label, marginBottom: 4 }}>Opening Balance</p>
            {snapshotDetail.positionName && (
              <DetailRow label="Participant" value={snapshotDetail.positionName} />
            )}
            <DetailRow
              label="Amount"
              value={snapshotDetail.openingBalance != null
                ? `${Number(snapshotDetail.openingBalance).toLocaleString('en-US', {
                    style: 'currency',
                    currency: snapshotDetail.currencyCode ?? 'USD',
                  })}`
                : '—'}
            />
            <DetailRow label="Currency" value={snapshotDetail.currencyCode ?? '—'} />
          </div>
        )}

        {/* S895 gap note — detail endpoint not yet built */}
        {!detailLoading && !snapshotDetail && !detailError && (
          <div style={S.infoBox}>
            Opening balance detail will appear here once the draw authorization
            detail endpoint is wired (S895 follow-up). Authorization ID:{' '}
            <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{authorizationId}</span>
          </div>
        )}

        <div style={S.divider} />

        {/* Eligibility */}
        {isSodBlocked && (
          <div style={S.warnBox} role="alert">
            {SOD_COPY}
          </div>
        )}

        {isEligible && (
          <div style={S.infoBox}>
            You are eligible to approve this draw ledger initialization.
            Approving will activate the draw authorization and write the
            opening balance entry to the draw ledger.
          </div>
        )}

        {/* Approve error */}
        {approveError && (
          <div style={S.errorBox} role="alert">
            {approveError}
          </div>
        )}

        {/* personUuid missing warning — detail endpoint not wired */}
        {!personUuid && !detailLoading && isEligible && (
          <div style={S.warnBox} role="alert">
            The participant's personUuid could not be resolved. The draw authorization
            detail endpoint must be implemented before approvals can be processed from
            this panel. Authorization ID:{' '}
            <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{authorizationId}</span>
          </div>
        )}

      </div>

      {/* Footer actions */}
      <div style={S.footer}>
        <button
          type="button"
          onClick={onDismiss}
          style={{ fontSize: 13, padding: '7px 16px', borderRadius: 6, border: '1px solid rgb(var(--color-border))', background: 'rgb(var(--color-surface))', cursor: 'pointer' }}
          disabled={approving}
        >
          Close
        </button>

        {/* WHY: only eligible items expose the approve action — server is authoritative */}
        {isEligible && (
          <button
            type="button"
            onClick={handleApprove}
            disabled={approving || !personUuid}
            title={!personUuid ? 'personUuid not resolved — detail endpoint required' : undefined}
            style={{
              fontSize: 13, fontWeight: 600, padding: '7px 18px', borderRadius: 6,
              border: 'none', background: 'rgb(var(--color-primary))', color: '#fff',
              cursor: approving || !personUuid ? 'not-allowed' : 'pointer',
              opacity: approving || !personUuid ? 0.6 : 1,
            }}
          >
            {approving ? 'Approving…' : 'Approve & initialize ledger'}
          </button>
        )}

        {isSodBlocked && (
          <button
            type="button"
            disabled
            title={SOD_COPY}
            style={{ fontSize: 13, padding: '7px 16px', borderRadius: 6, border: '1px solid rgb(var(--color-border))', background: 'rgb(var(--color-surface))', cursor: 'not-allowed', opacity: 0.6 }}
          >
            Another approver required
          </button>
        )}
      </div>

    </div>
  );
}

DrawLedgerApprovalReview.propTypes = {
  item: PropTypes.shape({
    itemId:          PropTypes.string.isRequired,
    itemType:        PropTypes.string.isRequired,
    planCode:        PropTypes.string,
    planName:        PropTypes.string,
    submittedBy:     PropTypes.string,
    submittedByName: PropTypes.string,
    submittedAt:     PropTypes.string,
    fiscalPeriodId:  PropTypes.number,
    callerEligibility: PropTypes.shape({
      eligible:      PropTypes.bool,
      blockedReason: PropTypes.string,
    }),
  }).isRequired,
  onApprove: PropTypes.func,
  onDismiss: PropTypes.func,
};

DrawLedgerApprovalReview.defaultProps = {
  onApprove: null,
  onDismiss: null,
};

export default DrawLedgerApprovalReview;
